import { contentPath, gcsPublicConfigured, gcsReadPublic, listPublished } from "../storage";
import { normalizeEntry } from "./entryModel";
import { splitFrontmatter } from "./frontmatter";
import { parseEntry } from "./serialize";

// Journal entries are read from the public bucket at runtime, where the admin
// publishes and deletes them; bundled copies would hide the admin's changes.

/**
 * Slugs of every live entry of a type (Journal entries in practice).
 * @returns {Promise<string[]>} [] when no bucket is configured; throws when
 *   the listing fails, so pages can show an error state.
 */
export async function discoverContent(type = "journal") {
	if (!gcsPublicConfigured()) return [];
	return (await listPublished(type)).map((i) => i.slug);
}

async function readMarkdown(type, slug) {
	if (!gcsPublicConfigured()) return null;
	return gcsReadPublic(contentPath("production", type, slug));
}

const objectLinks = (links) =>
	Array.isArray(links) ? links.filter((l) => l && typeof l === "object") : [];

function toEntry(type, slug, markdown) {
	if (splitFrontmatter(markdown).frontmatter === null) return null;
	const { metadata, body } = parseEntry(slug, markdown);
	const entry = {
		...normalizeEntry(type, slug, metadata, body),
		links: objectLinks(metadata.links),
	};
	return { metadata: entry, body };
}

/**
 * One live entry as normalizeEntry's shape plus `links`, or null when it is
 * missing, has no frontmatter, or cannot be read (the error is logged), so a
 * list can skip it.
 */
export async function loadContentFromSlug(type, slug) {
	try {
		return (await loadFullContent(type, slug))?.metadata ?? null;
	} catch (error) {
		console.error(`Error loading ${type}/${slug}:`, error);
		return null;
	}
}

/**
 * One live entry with its markdown body: `{ metadata, body }`, metadata as in
 * loadContentFromSlug. Null when missing or without frontmatter; rejects when
 * the read fails, so a page can tell "not found" from "could not load".
 */
export async function loadFullContent(type, slug) {
	const markdown = await readMarkdown(type, slug);
	if (markdown === null) return null;
	return toEntry(type, slug, markdown);
}

/**
 * Every published Journal entry, newest first. Entries that fail to load are
 * skipped; throws when the listing itself fails.
 */
export async function loadPublishedJournal() {
	const slugs = await discoverContent("journal");
	const entries = await Promise.all(slugs.map((s) => loadContentFromSlug("journal", s)));
	return entries
		.filter((e) => e && e.status === "published")
		.sort((a, b) => String(b.date).localeCompare(String(a.date)));
}

/**
 * The entries either side of `slug` in a newest-first list, for prev/next.
 * @returns {{ newer: object | null, older: object | null }} both null when slug is absent.
 */
export function neighbours(list, slug) {
	const at = list.findIndex((e) => e.slug === slug);
	if (at < 0) return { newer: null, older: null };
	return { newer: list[at - 1] ?? null, older: list[at + 1] ?? null };
}
